import { useState, useEffect, useCallback, Fragment } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronRight, ChevronDown, Download, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// NEXT BUILD PASS 1B (item 11) — SOR Accounts. Money in Indian format, a minus sign BEFORE the ₹.
const money = (v) => {
  const n = Number(v || 0);
  const s = `₹${Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  return n < 0 ? `−${s}` : s;
};
const money2 = (v) => {
  const n = Number(v || 0);
  const s = `₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `−${s}` : s;
};
const ddmmyyyy = (iso) => {
  const s = String(iso || "").slice(0, 10);
  return s.length === 10 ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : "—";
};
const PAGE = 10;

const Card = ({ label, value, sub, accent, testId }) => (
  <div className={`border rounded-sm px-4 py-3 ${accent ? "border-orange-200 bg-orange-50/60" : "border-zinc-200 bg-white"}`} data-testid={testId}>
    <div className={`text-[10px] font-semibold uppercase tracking-wide ${accent ? "text-orange-700" : "text-zinc-500"}`}>{label}</div>
    <div className={`font-mono text-xl font-semibold mt-1 ${accent ? "text-orange-700" : "text-zinc-900"}`}>{value}</div>
    {sub ? <div className="text-[10px] text-zinc-500 mt-1 leading-snug">{sub}</div> : null}
  </div>
);

const Pager = ({ page, pages, onPage, testId }) => pages <= 1 ? null : (
  <div className="flex items-center justify-end gap-1 px-3 py-2" data-testid={testId}>
    <button className="h-6 px-2 text-[10px] border border-zinc-200 rounded-sm" onClick={() => onPage(Math.max(1, page - 1))}>Prev</button>
    {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
      <button key={p} onClick={() => onPage(p)}
        className={`h-6 px-2 text-[10px] rounded-sm border ${p === page ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200"}`}>{p}</button>
    ))}
    <button className="h-6 px-2 text-[10px] border border-zinc-200 rounded-sm" onClick={() => onPage(Math.min(pages, page + 1))}>Next</button>
  </div>
);

export default function SorAccountsPage() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r)) || perms.includes("admin.full_access");
  const canExport = isAdmin || perms.includes("sor.export");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channelId, setChannelId] = useState("");
  const [dates, setDates] = useState({ date_from: "", date_to: "" });
  const [f, setFilters] = useState({ search: "", city: "", warehouse: "", age_band: "", month: "", payment: "", warehouse_status: "", min_owed: "" });
  const [openBand, setOpenBand] = useState("");
  const [bandPage, setBandPage] = useState(1);
  const [openMonth, setOpenMonth] = useState("");
  const [openWh, setOpenWh] = useState("");
  const [whPage, setWhPage] = useState(1);
  const [whInvPage, setWhInvPage] = useState(1);
  const [openInv, setOpenInv] = useState("");
  const [invPay, setInvPay] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...f, ...dates };
      if (channelId) params.channel_id = channelId;
      const { data: d } = await api.get("/sor/accounts", { params });
      setData(d);
      if (!channelId && d?.channel?.channel_id) setChannelId(d.channel.channel_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load SOR Accounts");
    } finally { setLoading(false); }
  }, [channelId, f, dates]);
  useEffect(() => { load(); }, [load]);

  const openPayments = async (r) => {
    if (openInv === r.order_id) { setOpenInv(""); return; }
    setOpenInv(r.order_id);
    if (!invPay[r.order_id]) {
      try {
        const { data: d } = await api.get(`/sor/invoice/${r.order_id}/payments`);
        setInvPay(p => ({ ...p, [r.order_id]: d }));
      } catch (e) { toast.error("Could not load the part-payments"); }
    }
  };

  const exportXl = async (section) => {
    try {
      const params = { ...f, ...dates, section, channel_id: channelId };
      const res = await api.get("/sor/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `SOR_${section}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not export"); }
  };

  const clearFilters = () => setFilters({ search: "", city: "", warehouse: "", age_band: "", month: "", payment: "", warehouse_status: "", min_owed: "" });

  if (loading && !data) return <div className="p-6 text-sm text-zinc-400" data-testid="sor-loading">Loading SOR Accounts…</div>;
  if (!data || !data.channel) return (
    <div className="p-6" data-testid="sor-empty">
      <h1 className="font-heading text-2xl font-semibold">SOR Accounts</h1>
      <p className="text-sm text-zinc-500 mt-2">No sales channel is on &quot;Sale or return&quot; yet. A customer joins this page when its sales channel&apos;s payment type is set to &quot;Sale or return&quot;.</p>
    </div>
  );

  const c = data.cards || {};
  const bandRows = openBand ? (data.invoices || []).filter(r => r.age_band === openBand).sort((a, b) => b.owed - a.owed) : [];
  const bandPages = Math.ceil(bandRows.length / PAGE) || 1;
  const whRows = data.warehouses || [];
  const whPages = Math.ceil(whRows.length / PAGE) || 1;
  const whInvoices = openWh ? (data.warehouse_rows?.[openWh] || []) : [];
  const whInvPages = Math.ceil(whInvoices.length / PAGE) || 1;
  const statusTone = { "Act now": "bg-red-50 text-red-700 border-red-200", Watch: "bg-amber-50 text-amber-700 border-amber-200", Healthy: "bg-emerald-50 text-emerald-700 border-emerald-200" };

  return (
    <div className="p-6 space-y-4" data-testid="sor-accounts-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Reports · Receivables</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">SOR Accounts</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">Sale-or-return customers. Every invoice stays a receivable until it is paid. Payments are matched to invoices the way the customer&apos;s own payment advice says.</p>
        </div>
        {canExport && (
          <Button className="rounded-sm h-8 text-xs" onClick={() => exportXl("view")} data-testid="sor-export-view">
            <Download size={13} className="mr-1" /> Export whole view
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-zinc-400">SOR customers</span>
        {(data.channels || []).map(ch => (
          <button key={ch.channel_id} onClick={() => { setChannelId(ch.channel_id); setOpenWh(""); setOpenBand(""); }}
            className={`h-7 px-3 rounded-full text-xs font-semibold border transition-colors ${ch.channel_id === data.channel.channel_id ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400"}`}
            data-testid={`sor-channel-${ch.channel_id}`}>
            {ch.name} · {ch.warehouses} warehouses
          </button>
        ))}
        <span className="text-[10px] text-zinc-400 ml-2">A customer joins this page when its sales channel&apos;s payment type is set to &quot;Sale or return&quot;.</span>
        <div className="flex items-center gap-1 ml-auto">
          <Input type="date" value={dates.date_from} onChange={e => setDates({ ...dates, date_from: e.target.value })} className="h-7 w-32 text-[11px] rounded-sm" data-testid="sor-date-from" />
          <span className="text-[10px] text-zinc-400">to</span>
          <Input type="date" value={dates.date_to} onChange={e => setDates({ ...dates, date_to: e.target.value })} className="h-7 w-32 text-[11px] rounded-sm" data-testid="sor-date-to" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="sor-cards">
        <Card label="Sent (invoiced)" value={money(c.sent)} sub={`${c.invoices || 0} invoices · ${c.period_label || "all"}`} testId="sor-card-sent" />
        <Card label="Paid" value={money(c.paid)} sub="from the platform payment advice, invoice by invoice" testId="sor-card-paid" />
        <Card label="Debit notes & deductions" value={money(c.deductions)}
          sub={`+ TDS ${money(c.tds)} (not a cost) · + credit notes ${money(c.credit_notes)}`} testId="sor-card-deductions" />
        <Card label="Returns (PRN)" value={money(c.returns_prn)} sub={`${c.returns_prn_lines || 0} line(s), for information`} testId="sor-card-prn" />
        <Card label="Still owed" value={money(c.owed)} accent
          sub={`${c.owed_invoices || 0} invoices · ${c.recovered_pct || 0}% of everything sent is recovered`} testId="sor-card-owed" />
        <Card label="Our cost of goods sent" value={money(c.cost_of_goods_sent)} sub="frozen COGS × units billed" testId="sor-card-cost" />
      </div>

      <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="sor-filters">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-zinc-900">Filters — apply to every section below</h2>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-zinc-500" data-testid="sor-filter-summary">
              {data.summary?.unpaid_invoices || 0} unpaid invoices · {money(data.summary?.owed)} still owed
            </span>
            <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]" onClick={clearFilters} data-testid="sor-clear-filters">
              <X size={11} className="mr-1" /> Clear filters
            </Button>
            {canExport && <Button size="sm" className="h-7 rounded-sm text-[11px]" onClick={() => exportXl("view")} data-testid="sor-export-this-view">Excel — this view</Button>}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <div><label className="text-[10px] text-zinc-500">Search order, invoice or warehouse</label>
            <Input value={f.search} onChange={e => setFilters({ ...f, search: e.target.value })} placeholder="e.g. ORD-00327 or Patna" className="h-8 text-xs rounded-sm mt-0.5" data-testid="sor-f-search" /></div>
          <div><label className="text-[10px] text-zinc-500">City</label>
            <select value={f.city} onChange={e => setFilters({ ...f, city: e.target.value })} className="mt-0.5 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="sor-f-city">
              <option value="">All cities</option>
              {(data.options?.cities || []).map(x => <option key={x} value={x}>{x}</option>)}
            </select></div>
          <div><label className="text-[10px] text-zinc-500">Warehouse</label>
            <select value={f.warehouse} onChange={e => setFilters({ ...f, warehouse: e.target.value })} className="mt-0.5 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="sor-f-warehouse">
              <option value="">All warehouses</option>
              {(data.options?.warehouses || []).map(x => <option key={x} value={x}>{x}</option>)}
            </select></div>
          <div><label className="text-[10px] text-zinc-500">Age</label>
            <select value={f.age_band} onChange={e => setFilters({ ...f, age_band: e.target.value })} className="mt-0.5 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="sor-f-age">
              <option value="">All ages</option>
              {(data.ageing || []).map(b => <option key={b.band} value={b.band}>{b.label}</option>)}
            </select></div>
          <div><label className="text-[10px] text-zinc-500">Invoice month</label>
            <select value={f.month} onChange={e => setFilters({ ...f, month: e.target.value })} className="mt-0.5 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="sor-f-month">
              <option value="">All months</option>
              {(data.options?.months || []).map(m => <option key={m.month} value={m.month}>{m.label}</option>)}
            </select></div>
          <div><label className="text-[10px] text-zinc-500">Payment</label>
            <select value={f.payment} onChange={e => setFilters({ ...f, payment: e.target.value })} className="mt-0.5 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="sor-f-payment">
              <option value="">Any</option>
              <option value="none">No payment yet</option>
              <option value="part">Part-paid</option>
            </select></div>
          <div><label className="text-[10px] text-zinc-500">Warehouse status</label>
            <select value={f.warehouse_status} onChange={e => setFilters({ ...f, warehouse_status: e.target.value })} className="mt-0.5 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2" data-testid="sor-f-status">
              <option value="">All</option>
              <option value="Act now">Act now</option>
              <option value="Watch">Watch</option>
              <option value="Healthy">Healthy</option>
            </select></div>
          <div><label className="text-[10px] text-zinc-500">Owed at least (₹)</label>
            <Input type="number" value={f.min_owed} onChange={e => setFilters({ ...f, min_owed: e.target.value })} placeholder="0" className="h-8 text-xs rounded-sm mt-0.5" data-testid="sor-f-min-owed" /></div>
        </div>
      </div>

      {/* AGEING */}
      <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="sor-ageing">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-zinc-900">Money stuck, by age — click a band to see its invoices</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5 max-w-3xl">Use it to decide where to push sales. Up to 60 days is normal for SOR. 61–90 days: check sell-through and plan a scheme. Over 90 days: push secondary sales with schemes or discounts, and focus on liquidation.</p>
          </div>
          {canExport && <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]" onClick={() => exportXl("ageing")} data-testid="sor-export-ageing">Excel</Button>}
        </div>
        <div className="mt-3 space-y-1">
          {(data.ageing || []).map(b => {
            const max = Math.max(...(data.ageing || []).map(x => x.owed), 1);
            const open = openBand === b.band;
            return (
              <Fragment key={b.band}>
                <button onClick={() => { setOpenBand(open ? "" : b.band); setBandPage(1); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 border rounded-sm text-left ${open ? "border-zinc-900" : "border-zinc-200"} hover:bg-zinc-50`}
                  data-testid={`sor-band-${b.band}`}>
                  {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  <span className="text-xs font-semibold w-24">{b.label}</span>
                  <span className="flex-1 h-2 bg-zinc-100 rounded-sm overflow-hidden">
                    <span className="block h-full bg-zinc-700" style={{ width: `${Math.round((b.owed / max) * 100)}%` }} />
                  </span>
                  <span className="font-mono text-xs font-semibold w-28 text-right">{money(b.owed)}</span>
                  <span className="text-[11px] text-zinc-500 w-16 text-right">{b.invoices} inv</span>
                  <span className="text-[11px] text-zinc-600 w-72">{b.note}</span>
                </button>
                {open && (
                  <div className="border border-zinc-200 rounded-sm overflow-auto" data-testid={`sor-band-rows-${b.band}`}>
                    <table className="w-full text-[11px]">
                      <thead className="bg-zinc-50 text-left">
                        <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-semibold [&>th]:text-zinc-600">
                          <th>Warehouse</th><th>Order</th><th>Invoice</th><th>Invoice date</th>
                          <th className="text-right">Days</th><th className="text-right">Invoice ₹</th>
                          <th className="text-right">Still owed</th><th className="text-right">Part-payments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bandRows.slice((bandPage - 1) * PAGE, bandPage * PAGE).map(r => (
                          <tr key={r.order_id} className="border-t border-zinc-100 [&>td]:px-3 [&>td]:py-1.5" data-testid={`sor-band-row-${r.order_number}`}>
                            <td>{r.warehouse}</td><td className="font-mono">{r.order_number}</td>
                            <td className="font-mono">{r.invoice_number || "—"}</td><td>{ddmmyyyy(r.invoice_date)}</td>
                            <td className="text-right">{r.age_days}</td>
                            <td className="text-right font-mono">{money(r.sent)}</td>
                            <td className="text-right font-mono font-semibold">{money(r.owed)}</td>
                            <td className="text-right text-zinc-500">{r.payment_count ? `${r.payment_count} payments` : "none yet"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-zinc-500 px-3 py-2">Showing {bandRows.length ? (bandPage - 1) * PAGE + 1 : 0}–{Math.min(bandPage * PAGE, bandRows.length)} of {bandRows.length}</span>
                      <Pager page={bandPage} pages={bandPages} onPage={setBandPage} testId={`sor-band-pager-${b.band}`} />
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>

      {/* BY MONTH */}
      <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="sor-months">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-zinc-900">Sent vs still owed, by invoice month</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">Click a month to see which warehouses still owe for it.</p>
          </div>
          {canExport && <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]" onClick={() => exportXl("months")} data-testid="sor-export-months">Excel</Button>}
        </div>
        <div className="mt-3 border border-zinc-200 rounded-sm overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-left">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-semibold [&>th]:text-zinc-600">
                <th className="w-8"></th><th>Month</th><th className="text-right">Invoices</th>
                <th className="text-right">Sent</th><th className="text-right">Still owed</th><th className="text-right">Recovered</th>
              </tr>
            </thead>
            <tbody>
              {(data.months || []).length === 0 ? <tr><td colSpan={6} className="text-center py-6 text-zinc-400">No invoices for these filters</td></tr> :
                (data.months || []).map(m => (
                  <Fragment key={m.month}>
                    <tr className="border-t border-zinc-100 [&>td]:px-3 [&>td]:py-2 cursor-pointer hover:bg-zinc-50"
                      onClick={() => setOpenMonth(openMonth === m.month ? "" : m.month)} data-testid={`sor-month-${m.month}`}>
                      <td>{openMonth === m.month ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                      <td className="font-semibold">{m.label}</td>
                      <td className="text-right">{m.invoices}</td>
                      <td className="text-right font-mono">{money(m.sent)}</td>
                      <td className="text-right font-mono font-semibold">{money(m.owed)}</td>
                      <td className="text-right">{m.recovered_pct}%</td>
                    </tr>
                    {openMonth === m.month && (
                      <tr><td colSpan={6} className="bg-zinc-50/60 px-3 py-2">
                        <table className="w-full text-[11px]" data-testid={`sor-month-rows-${m.month}`}>
                          <thead><tr className="text-left [&>th]:py-1 [&>th]:font-semibold [&>th]:text-zinc-600"><th>Warehouse</th><th className="text-right">Unpaid invoices</th><th className="text-right">Still owed</th><th className="text-right">Oldest</th></tr></thead>
                          <tbody>
                            {(m.warehouses || []).length === 0 ? <tr><td colSpan={4} className="py-2 text-zinc-400">Nothing owed for this month</td></tr> :
                              (m.warehouses || []).map(w => (
                                <tr key={w.warehouse} className="border-t border-zinc-200/70 [&>td]:py-1">
                                  <td>{w.warehouse}</td>
                                  <td className="text-right">{w.unpaid_invoices}</td>
                                  <td className="text-right font-mono font-semibold">{money(w.owed)}</td>
                                  <td className="text-right">{w.oldest_days} days</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </td></tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* BY WAREHOUSE */}
      <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="sor-warehouses">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-zinc-900">By warehouse — every warehouse that still owes</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">Click a warehouse to see its unpaid invoices. Click an invoice to see every part-payment made against it.</p>
          </div>
          {canExport && <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]" onClick={() => exportXl("warehouses")} data-testid="sor-export-warehouses">Excel — warehouses</Button>}
        </div>
        <div className="mt-3 border border-zinc-200 rounded-sm overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-left">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-semibold [&>th]:text-zinc-600">
                <th className="w-8"></th><th>Warehouse</th><th className="text-right">Invoices</th><th className="text-right">Unpaid</th>
                <th className="text-right">Sent</th><th className="text-right">Paid</th><th className="text-right">Still owed</th>
                <th className="text-right">Recovered</th><th className="text-right">Oldest</th><th className="text-right">Owed 90+ days</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {whRows.length === 0 ? <tr><td colSpan={11} className="text-center py-6 text-zinc-400">No warehouse matches these filters</td></tr> :
                whRows.slice((whPage - 1) * PAGE, whPage * PAGE).map(w => (
                  <Fragment key={w.warehouse}>
                    <tr className="border-t border-zinc-100 [&>td]:px-3 [&>td]:py-2 cursor-pointer hover:bg-zinc-50"
                      onClick={() => { setOpenWh(openWh === w.warehouse ? "" : w.warehouse); setWhInvPage(1); }}
                      data-testid={`sor-wh-${w.warehouse}`}>
                      <td>{openWh === w.warehouse ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                      <td className="font-medium">{w.warehouse}</td>
                      <td className="text-right">{w.invoices}</td>
                      <td className="text-right">{w.unpaid}</td>
                      <td className="text-right font-mono">{money(w.sent)}</td>
                      <td className="text-right font-mono">{money(w.paid)}</td>
                      <td className="text-right font-mono font-semibold">{money(w.owed)}</td>
                      <td className="text-right">{w.recovered_pct}%</td>
                      <td className="text-right">{w.oldest_unpaid_days} d</td>
                      <td className="text-right font-mono">{money(w.owed_over_90)}</td>
                      <td><span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border ${statusTone[w.status]}`} data-testid={`sor-wh-status-${w.warehouse}`}>{w.status}</span></td>
                    </tr>
                    {openWh === w.warehouse && (
                      <tr><td colSpan={11} className="bg-zinc-50/60 px-3 py-2">
                        <table className="w-full text-[11px]" data-testid={`sor-wh-invoices-${w.warehouse}`}>
                          <thead><tr className="text-left [&>th]:py-1 [&>th]:font-semibold [&>th]:text-zinc-600">
                            <th className="w-6"></th><th>Order</th><th>Invoice</th><th>Invoice date</th><th className="text-right">Days</th>
                            <th className="text-right">Invoice ₹</th><th className="text-right">Paid so far</th><th className="text-right">Still owed</th><th className="text-right">Part-payments</th>
                          </tr></thead>
                          <tbody>
                            {whInvoices.slice((whInvPage - 1) * PAGE, whInvPage * PAGE).map(r => (
                              <Fragment key={r.order_id}>
                                <tr className="border-t border-zinc-200/70 [&>td]:py-1 cursor-pointer" onClick={() => openPayments(r)} data-testid={`sor-inv-${r.order_number}`}>
                                  <td>{openInv === r.order_id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
                                  <td className="font-mono">{r.order_number}</td>
                                  <td className="font-mono">{r.invoice_number || "—"}</td>
                                  <td>{ddmmyyyy(r.invoice_date)}</td>
                                  <td className="text-right">{r.age_days}</td>
                                  <td className="text-right font-mono">{money(r.sent)}</td>
                                  <td className="text-right font-mono">{money(r.paid)}</td>
                                  <td className="text-right font-mono font-semibold">{money(r.owed)}</td>
                                  <td className="text-right text-zinc-500">{r.payment_count ? `${r.payment_count} payments` : "none yet"}</td>
                                </tr>
                                {openInv === r.order_id && (
                                  <tr><td colSpan={9} className="py-2 px-2 bg-white" data-testid={`sor-payments-${r.order_number}`}>
                                    <div className="text-[11px] text-zinc-700">{invPay[r.order_id]?.line || "Loading…"}</div>
                                    <div className="flex flex-wrap gap-2 mt-1.5">
                                      {(invPay[r.order_id]?.payments || []).map((p, i) => (
                                        <span key={i} className="border border-zinc-200 rounded-sm px-2 py-1 text-[10px] font-mono">
                                          {p.date_display || ddmmyyyy(p.date)}<br />{money2(p.amount)}{p.reference ? <><br /><span className="text-zinc-500">{p.reference}</span></> : null}
                                        </span>
                                      ))}
                                    </div>
                                  </td></tr>
                                )}
                              </Fragment>
                            ))}
                          </tbody>
                        </table>
                        <Pager page={whInvPage} pages={whInvPages} onPage={setWhInvPage} testId={`sor-wh-inv-pager-${w.warehouse}`} />
                      </td></tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
          </table>
          <Pager page={whPage} pages={whPages} onPage={setWhPage} testId="sor-wh-pager" />
        </div>
        <p className="text-[10px] text-zinc-400 mt-2">Status rule: &quot;Act now&quot; = under 25% recovered with invoices 30+ days old, or more than ₹1 lakh owed for 90+ days. &quot;Watch&quot; = under 70% recovered.</p>
      </div>

      {/* ALERTS */}
      <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="sor-alerts">
        <h2 className="text-base font-semibold text-zinc-900">Alerts (no order is ever blocked)</h2>
        <div className="mt-3 space-y-2">
          {(data.alerts || []).length === 0 ? <p className="text-xs text-zinc-400" data-testid="sor-alerts-empty">Nothing needs action right now.</p> :
            (data.alerts || []).map(a => (
              <div key={a.warehouse} className="border border-red-200 bg-red-50/60 rounded-sm px-3 py-2" data-testid={`sor-alert-${a.warehouse}`}>
                <div className="text-xs text-red-800">{a.text}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
